"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.orderListQuerySchema = exports.createOrderSchema = exports.orderLineSchema = exports.productListQuerySchema = exports.adjustStockSchema = exports.updateProductSchema = exports.createProductSchema = exports.updateTagSchema = exports.createTagSchema = exports.updateProductCategorySchema = exports.createProductCategorySchema = exports.STOCK_MOVEMENT_REASONS = exports.ORDER_STATUSES = exports.ORDER_SOURCES = exports.ORDER_DESTINATION_TYPES = exports.SERVICE_CLASSES = exports.PRODUCT_TYPES = void 0;
exports.resolveItemPrinter = resolveItemPrinter;
exports.splitOrderIntoTickets = splitOrderIntoTickets;
exports.splitOrdersIntoTickets = splitOrdersIntoTickets;
exports.buildStationRouting = buildStationRouting;
const zod_1 = require("zod");
/**
 * Cafe contracts — SPEC §5.4 (Cafe), §6.2 (Product Categories / Tags),
 * §6.3 (Products) and §10 (station routing).
 *
 * The enums mirror the Prisma enums in `@gc/db`. They are duplicated rather
 * than imported because the web apps must not pull in the Prisma client, which
 * is server-only; `cafe.contract.test.ts` in the API asserts the two stay in
 * step, so drift fails the build rather than shipping.
 *
 * MONEY OVER THE WIRE: every `*Md` field is a bigint of millidinars on the
 * server. JSON has no bigint, so it crosses as a decimal STRING and is parsed
 * back with `fromMillidinars()`. Never `Number()` these.
 *
 * The bottom half of this file is the **ticket splitter** — a pure function
 * with no I/O and no Prisma types, because the API writes the PrintJob rows and
 * the Print Bridge renders them, and both must agree on exactly how one order
 * becomes N station tickets.
 */
/* -------------------------------------------------------------------------- */
/* Enums                                                                      */
/* -------------------------------------------------------------------------- */
/** SPEC §6.3 "Type (e.g. Numeric)". Only NUMERIC is owner-confirmed. */
exports.PRODUCT_TYPES = ['NUMERIC', 'WEIGHED', 'SERVICE'];
/**
 * Which checkout discount bucket a category's products bill under — the one
 * list, imported by the API's validation, the categories screen and the
 * schemas below.
 *
 * It was four separate copies when SHISHA was added, and the screen's copy
 * shipped a day before the controller's: the dropdown offered Shisha and the
 * API answered 400 on it. A bucket the till can charge but the API refuses to
 * file is worse than no bucket, so there is one list and everything reads it.
 *
 * Mirrors the `ServiceClass` enum in `packages/db/prisma/schema.prisma`.
 */
exports.SERVICE_CLASSES = ['CAFE', 'KITCHEN', 'SHISHA'];
/**
 * SPEC §5.4 — an order is destined for the tab as a whole, one device, or one
 * table. See the `Order` model comment in the schema for the nullable-FK
 * invariant this discriminates.
 */
exports.ORDER_DESTINATION_TYPES = ['TAB', 'DEVICE', 'TABLE'];
/** Staff-entered (SPEC §5.4) or placed from the in-PC portal Shop (SPEC §9). */
exports.ORDER_SOURCES = ['STAFF', 'PORTAL'];
exports.ORDER_STATUSES = ['PENDING', 'PREPARING', 'SERVED', 'CANCELLED'];
exports.STOCK_MOVEMENT_REASONS = [
    'OPENING_BALANCE',
    'MANUAL_ADJUSTMENT',
    'SALE',
    'SALE_REVERSAL',
    'PURCHASE',
    'WASTAGE',
];
/* -------------------------------------------------------------------------- */
/* Write models — taxonomy                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Names are stored and compared as entered. `.trim()` only strips surrounding
 * whitespace; it does not normalise Arabic presentation forms, because two
 * visually similar spellings are two different products to the venue.
 */
const entityName = zod_1.z.string().trim().min(1, 'Name is required.').max(80);
/** Empty string from a "no printer" <select> means "inherit", not "printer ''". */
const optionalId = zod_1.z
    .string()
    .trim()
    .max(40)
    .nullable()
    .optional()
    .transform((value) => (value ? value : null));
/**
 * The PATCH twin of {@link optionalId}: an OMITTED key must stay `undefined`
 * (Prisma leaves the column alone) while null/'' still clears. The create
 * shape's transform null-fills omission — fine on create, but on an update it
 * would clear a category's image every time staff renamed it (wave-3 review
 * FIX-2 observation on the explicit update schemas).
 */
const optionalIdUpdate = zod_1.z
    .string()
    .trim()
    .max(40)
    .nullable()
    .optional()
    .transform((value) => (value === undefined ? undefined : value ? value : null));
exports.createProductCategorySchema = zod_1.z.object({
    name: entityName,
    printerId: optionalId,
    imageMediaAssetId: optionalId,
    /**
     * Wave 3 — which discount bucket this category's products bill under.
     * OPTIONAL WITHOUT A DEFAULT on purpose: `updateProductCategorySchema` is
     * `.partial()` of this object, and a zod default would survive the partial
     * (the house zod-4 trap) — absent falls to the DB default (CAFE).
     */
    serviceClass: zod_1.z.enum(exports.SERVICE_CLASSES).optional(),
    sortOrder: zod_1.z.number().int().min(0).default(0),
});
/**
 * Explicit — NOT `.partial()` of the create schema: zod 4 keeps `.default()`
 * through `.partial()`, so the derived version silently sent `sortOrder: 0`
 * on every PATCH and a rename flattened the whole menu ordering (wave-3
 * review R1#4/R4#4). The house rule everywhere else, applied here too.
 */
exports.updateProductCategorySchema = zod_1.z.object({
    name: entityName.optional(),
    printerId: optionalIdUpdate,
    imageMediaAssetId: optionalIdUpdate,
    serviceClass: zod_1.z.enum(exports.SERVICE_CLASSES).optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
exports.createTagSchema = zod_1.z.object({
    name: entityName,
    printerId: optionalId,
    sortOrder: zod_1.z.number().int().min(0).default(0),
});
/** Explicit for the same reason as `updateProductCategorySchema` above. */
exports.updateTagSchema = zod_1.z.object({
    name: entityName.optional(),
    printerId: optionalIdUpdate,
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
/* -------------------------------------------------------------------------- */
/* Write models — products                                                    */
/* -------------------------------------------------------------------------- */
/** Money accepted from clients as whole IQD, converted server-side. */
const iqdAmount = zod_1.z
    .number()
    .int('Prices are whole dinars — IQD has no useful sub-unit.')
    .min(0, 'Amount cannot be negative.')
    .max(1_000_000_000);
/**
 * SPEC §6.3 form: image, name, category, tag(s), type, barcode, cost, price,
 * opening stock. Barcode is optional — a venue may scan or may not.
 */
exports.createProductSchema = zod_1.z
    .object({
    name: entityName,
    type: zod_1.z.enum(exports.PRODUCT_TYPES).default('NUMERIC'),
    barcode: zod_1.z.string().trim().max(64).nullable().optional(),
    imageMediaAssetId: optionalId,
    productCategoryId: optionalId,
    tagIds: zod_1.z.array(zod_1.z.string().trim().min(1)).max(10).default([]),
    cost: iqdAmount.default(0),
    price: iqdAmount.default(0),
    openingBalance: zod_1.z.number().int().min(0).max(1_000_000).default(0),
    lowStockThreshold: zod_1.z.number().int().min(0).max(1_000_000).nullable().optional(),
    /** Wave 3 — false: a sale touches no stock at all. Absent → DB default true. */
    trackStock: zod_1.z.boolean().optional(),
    isActive: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.number().int().min(0).default(0),
})
    // Selling below cost is a real thing (clearance, staff drinks), so this is
    // not an error — but it is almost always a typo on a NEW product, and the
    // Stores margin report is unreadable when it happens by accident.
    .refine((value) => value.price >= value.cost || value.price === 0, {
    message: 'Price is below cost. Set price to 0 if this item is not sold.',
    path: ['price'],
});
/**
 * Partial update. Deliberately NOT `createProductSchema.partial()`: the
 * price-vs-cost refinement cannot run on a partial object (either field may be
 * absent), so it is re-checked server-side against the merged row instead.
 */
exports.updateProductSchema = zod_1.z.object({
    name: entityName.optional(),
    type: zod_1.z.enum(exports.PRODUCT_TYPES).optional(),
    barcode: zod_1.z.string().trim().max(64).nullable().optional(),
    /*
     * `optionalIdUpdate`, NOT `optionalId` — the same trap the category schema
     * already documents. `optionalId` folds an omitted field to null, so a PATCH
     * that only changes a price would clear the picture and un-file the product.
     * `updateProduct` in the service already guards `=== undefined` on both;
     * with `optionalId` that guard could never fire, because the value arrived
     * as null every time.
     */
    imageMediaAssetId: optionalIdUpdate,
    productCategoryId: optionalIdUpdate,
    tagIds: zod_1.z.array(zod_1.z.string().trim().min(1)).max(10).optional(),
    cost: iqdAmount.optional(),
    price: iqdAmount.optional(),
    lowStockThreshold: zod_1.z.number().int().min(0).max(1_000_000).nullable().optional(),
    /** Wave 3 — see createProductSchema. */
    trackStock: zod_1.z.boolean().optional(),
    isActive: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
/**
 * SPEC §6.3 "Opening Balance (+ to adjust stock)" — the ⊕ in the products
 * table. A signed delta rather than a new absolute count, so two staff members
 * counting the same shelf at once cannot silently overwrite each other.
 */
exports.adjustStockSchema = zod_1.z.object({
    delta: zod_1.z
        .number()
        .int('Stock is counted in whole units.')
        .refine((value) => value !== 0, 'Enter an amount to add or remove.'),
    reason: zod_1.z.enum(exports.STOCK_MOVEMENT_REASONS).default('MANUAL_ADJUSTMENT'),
    note: zod_1.z.string().trim().max(200).nullable().optional(),
});
/** SPEC §6.3 header: search by name, Select Categories, Select Tags. */
exports.productListQuerySchema = zod_1.z.object({
    /** Matches product name OR barcode, so a scanner gun works in this box. */
    search: zod_1.z.string().trim().max(80).optional(),
    productCategoryId: zod_1.z.string().trim().min(1).optional(),
    tagId: zod_1.z.string().trim().min(1).optional(),
    /** Cafe menu mode: only sellable, in-catalogue items. */
    onlyActive: zod_1.z.coerce.boolean().default(false),
    lowStockOnly: zod_1.z.coerce.boolean().default(false),
});
/* -------------------------------------------------------------------------- */
/* Write models — orders                                                      */
/* -------------------------------------------------------------------------- */
exports.orderLineSchema = zod_1.z.object({
    productId: zod_1.z.string().trim().min(1, 'Pick a product.'),
    quantity: zod_1.z.number().int().min(1, 'Quantity must be at least 1.').max(999),
    note: zod_1.z.string().trim().max(200).nullable().optional(),
});
/**
 * Place a cafe order (SPEC §5.4).
 *
 * The destination invariant from the `Order` model is enforced here so a bad
 * payload never reaches the database:
 *   TAB    -> tabId required, destinationDeviceId must be absent
 *   DEVICE -> destinationDeviceId required (tabId normally also set)
 *   TABLE  -> destinationDeviceId required, pointing at a TABLE-type Device
 *
 * That a TABLE destination is a Device row is not an accident: SPEC §6.2 seeds
 * "Tables" as a Device Category, so tables are Devices priced at zero and the
 * two destinations share one FK.
 */
exports.createOrderSchema = zod_1.z
    .object({
    destinationType: zod_1.z.enum(exports.ORDER_DESTINATION_TYPES),
    tabId: zod_1.z.string().trim().min(1).nullable().optional(),
    destinationDeviceId: zod_1.z.string().trim().min(1).nullable().optional(),
    items: zod_1.z.array(exports.orderLineSchema).min(1, 'Add at least one item.').max(100),
    /** Whole-order discount in IQD; needs `discounts.apply` (SPEC §3). */
    discountIqd: iqdAmount.default(0),
    note: zod_1.z.string().trim().max(300).nullable().optional(),
    /**
     * SEND THIS BACK, not out: two burgers went to a table that ordered one.
     *
     * The whole order reverses — quantities go negative, stock comes back, the
     * bill credits the tab and the station gets a ticket marked RETURN reading
     * `Qty = -1`. Needs `cafe.return`, which is audited: an unwatched negative
     * sale is how a till is robbed.
     *
     * A FLAG ON THE ORDER, not a sign on each line, and deliberately so. One
     * order cannot be half sale and half return, because the ticket that
     * describes it would read "+1 burger, −1 burger" and the kitchen could not
     * act on it. Quantities in `items` therefore stay positive whichever this
     * is, and the server applies the sign.
     */
    isReturn: zod_1.z.boolean().default(false),
})
    .refine((value) => !value.isReturn || (value.discountIqd ?? 0) === 0, {
    // A discount on a credit is a discount on money going the other way: it
    // would hand back LESS than was taken, and the arithmetic reads as theft
    // in either direction. Returns reverse exactly what was charged.
    message: 'A return cannot carry a discount.',
    path: ['discountIqd'],
})
    .refine((value) => value.destinationType !== 'TAB' || !!value.tabId, {
    message: 'Choose which tab this order belongs to.',
    path: ['tabId'],
})
    .refine((value) => value.destinationType !== 'TAB' || !value.destinationDeviceId, {
    // The schema's invariant: a tab-wide order must not also name a device,
    // or the receipt cannot tell whether it belongs to the party or the seat.
    message: 'A tab-wide order cannot also target a device.',
    path: ['destinationDeviceId'],
})
    .refine((value) => value.destinationType === 'TAB' || !!value.destinationDeviceId, {
    message: 'Choose a device or table for this order.',
    path: ['destinationDeviceId'],
});
/** Bills → Cashier / Portal Orders list filters (SPEC §5.6). */
exports.orderListQuerySchema = zod_1.z.object({
    tabId: zod_1.z.string().trim().min(1).optional(),
    status: zod_1.z.enum(exports.ORDER_STATUSES).optional(),
    source: zod_1.z.enum(exports.ORDER_SOURCES).optional(),
    /** `YYYY-MM-DD` business date — reports never filter on createdAt (§4.1). */
    businessDate: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * Resolve the station for one line: tag, else category, else venue default.
 *
 * Exported because the API stores the resolved printer on `OrderItem.printerId`
 * at submit time, and it must use exactly the rule the splitter used.
 */
function resolveItemPrinter(item, routing) {
    for (const tagId of item.tagIds) {
        const printer = routing.tagPrinters.get(tagId);
        if (printer)
            return printer;
    }
    if (item.productCategoryId) {
        const printer = routing.categoryPrinters.get(item.productCategoryId);
        if (printer)
            return printer;
    }
    return routing.defaultPrinter;
}
/**
 * Split ONE order into one ticket per station (SPEC §5.4, §10).
 *
 * A matcha tagged Barista → BAR and a burger tagged Kitchen → KITCHEN in the
 * same order produce two tickets, each listing only its own station's items.
 *
 * Pure: no I/O, no Prisma, no clock. Everything it needs is in its arguments,
 * which is what lets the API unit-test the routing rule and the Print Bridge
 * re-run it against a stored payload.
 *
 * Ticket order is stable — by printer name, then printer id — so a reprint
 * hands the stations their tickets in the same sequence as the original, and
 * the tests do not depend on Map iteration order. Item order inside a ticket
 * follows the order the customer said them in.
 */
function splitOrderIntoTickets(order, routing) {
    const byPrinter = new Map();
    const unroutable = [];
    for (const item of order.items) {
        const printer = resolveItemPrinter(item, routing);
        if (!printer) {
            unroutable.push({
                orderId: order.id,
                orderItemId: item.id,
                name: item.name,
                quantity: item.quantity,
            });
            continue;
        }
        let ticket = byPrinter.get(printer.id);
        if (!ticket) {
            ticket = {
                printerId: printer.id,
                printerName: printer.name,
                orderId: order.id,
                destination: order.destination,
                tabName: order.tabName,
                placedAt: order.placedAt,
                items: [],
                note: order.note ?? null,
                isReturn: order.isReturn ?? false,
            };
            byPrinter.set(printer.id, ticket);
        }
        // `items` is readonly to callers; the splitter owns the array while it
        // builds it, which keeps the public shape immutable without a second pass.
        ticket.items.push({
            orderItemId: item.id,
            name: item.name,
            quantity: item.quantity,
            note: item.note ?? null,
        });
    }
    const tickets = [...byPrinter.values()].sort((a, b) => a.printerName.localeCompare(b.printerName) || a.printerId.localeCompare(b.printerId));
    return { tickets, unroutable };
}
/**
 * Split a BATCH of orders — a whole tab's worth, or everything queued while a
 * printer was offline.
 *
 * Tickets are never merged across orders even when two orders share a station:
 * each order has its own destination, tab name and time, all three of which
 * SPEC §5.4 requires on the ticket. Two orders for different tables that both
 * route to BAR therefore yield two BAR tickets, which is also what the barista
 * needs in order to plate them separately.
 */
function splitOrdersIntoTickets(orders, routing) {
    const tickets = [];
    const unroutable = [];
    for (const order of orders) {
        const plan = splitOrderIntoTickets(order, routing);
        tickets.push(...plan.tickets);
        unroutable.push(...plan.unroutable);
    }
    return { tickets, unroutable };
}
/**
 * Build a {@link StationRouting} from plain rows, so callers do not each hand-
 * roll the Maps. `printer: null` on a row means "this tag/category does not
 * route anywhere itself" and falls through to the next rule.
 */
function buildStationRouting(input) {
    const tagPrinters = new Map();
    for (const tag of input.tags)
        if (tag.printer)
            tagPrinters.set(tag.id, tag.printer);
    const categoryPrinters = new Map();
    for (const category of input.categories) {
        if (category.printer)
            categoryPrinters.set(category.id, category.printer);
    }
    return { tagPrinters, categoryPrinters, defaultPrinter: input.defaultPrinter };
}
//# sourceMappingURL=cafe.js.map